class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    // Increment request counter to track the latest request
    FacetFiltersForm.latestRenderRequestId = (FacetFiltersForm.latestRenderRequestId || 0) + 1;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    FacetFiltersForm.toggleCollectionLoading(true, 'renderPage');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    // Capture the request ID at the time of the fetch call
    const requestId = FacetFiltersForm.latestRenderRequestId;
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        // Skip processing if a newer request has been made (prevents stale responses from overwriting)
        if (requestId !== FacetFiltersForm.latestRenderRequestId) return;
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        // Custom dimension range filter: Restore range values after AJAX content is rendered
        if (typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(html.innerHTML);
        }
        setTimeout(() => {
          FacetFiltersForm.restoreDimensionRange();
          FacetFiltersForm.updateDimensionRangeDisplayValue();
        }, 100);
      })
      .catch((error) => {
        console.error('[facets.js] renderSectionFromFetch error', { url, error });
      })
      .finally(() => {
        FacetFiltersForm.toggleCollectionLoading(false, 'renderSectionFromFetch.finally');
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    // Custom dimension range filter: Restore range values after cached content is rendered
    setTimeout(() => {
      FacetFiltersForm.restoreDimensionRange();
      FacetFiltersForm.updateDimensionRangeDisplayValue();
    }, 100);
    FacetFiltersForm.toggleCollectionLoading(false, 'renderSectionFromCache');
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    productGridContainer.innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    productGridContainer.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });

    // Re-initialize Ajaxinate after content update, if available
    if (typeof window.initializeCollectionAjaxinate === 'function') {
      setTimeout(() => {
        window.initializeCollectionAjaxinate();
      }, 100);
    }
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);

    // Update dimension range display value after a small delay to ensure DOM is updated
    setTimeout(() => {
      FacetFiltersForm.updateDimensionRangeDisplayValue();
    }, 50);
  }

  /**
   * Helper function to parse a dimension text value into min/max numbers
   */
  static parseDimensionValue(valStr) {
    if (!valStr) return null;
    const parts = valStr.trim().split(/\s*-\s*/);
    const val0 = parseInt(parts[0], 10);
    if (isNaN(val0)) return null;
    const val1 = parts.length > 1 ? parseInt(parts[1], 10) : NaN;
    return { min: val0, max: isNaN(val1) ? val0 : val1 };
  }

  /**
   * Custom dimension range filter: Update the displayed value in the first visible facet-remove button
   * Gets the minimum value from the first visible button's data-dimension-value
   * Gets the maximum value from all active buttons or range inputs
   * Displays "min - max" format
   * Updates both desktop and mobile buttons separately
   */
  static updateDimensionRangeDisplayValue() {
    // If we have a stored range value, use it as the authoritative source
    // This prevents stale AJAX responses from overwriting the display
    if (FacetFiltersForm.dimensionRangeValue) {
      const displayMinValue = FacetFiltersForm.dimensionRangeValue.min;
      const maxValue = FacetFiltersForm.dimensionRangeValue.max;

      const updateButton = (container) => {
        if (!container) return;
        const firstVisibleButton = container.querySelector('facet-remove[data-dimension-item]:not(.hidden)');
        if (firstVisibleButton) {
          const dataValueSpan = firstVisibleButton.querySelector('span[data-value]');
          if (dataValueSpan) {
            dataValueSpan.textContent = `${displayMinValue}" - ${maxValue}"`;
          }
        }
      };

      updateButton(document.querySelector('.active-facets-desktop'));
      updateButton(document.querySelector('.active-facets-mobile'));
      return;
    }

    // Fallback: derive values from the active filter pills in the DOM
    const firstVisibleButton = document.querySelector('facet-remove[data-dimension-item]:not(.hidden)');
    if (!firstVisibleButton) return;

    let minValue = null;
    const firstButtonValue = firstVisibleButton.getAttribute('data-dimension-value');
    if (firstButtonValue) {
      const parsed = FacetFiltersForm.parseDimensionValue(firstButtonValue);
      if (parsed) minValue = parsed.min;
    }

    if (minValue === null) return;

    let maxValue = 0;
    const allDimensionItems = document.querySelectorAll('facet-remove[data-dimension-item]');
    allDimensionItems.forEach((item) => {
      const dimensionValue = item.getAttribute('data-dimension-value');
      if (dimensionValue) {
        const parsed = FacetFiltersForm.parseDimensionValue(dimensionValue);
        if (parsed && parsed.max > maxValue) {
          maxValue = parsed.max;
        }
      }
    });

    // Also check the current range input value
    const dimensionRangeContainers = document.querySelectorAll('.dimension-range-wrapper');
    dimensionRangeContainers.forEach((container) => {
      const controls = FacetFiltersForm.getDimensionRangeControls(container);
      if (controls.maxInput) {
        const currentMaxValue = parseInt(controls.maxInput.value, 10);
        if (!isNaN(currentMaxValue) && currentMaxValue > maxValue) {
          maxValue = currentMaxValue;
        }
      }
    });

    if (maxValue <= 0) return;

    const displayMinValue = minValue - 1;

    const updateButton = (container) => {
      if (!container) return;
      const firstVisibleBtn = container.querySelector('facet-remove[data-dimension-item]:not(.hidden)');
      if (firstVisibleBtn) {
        const dataValueSpan = firstVisibleBtn.querySelector('span[data-value]');
        if (dataValueSpan) {
          dataValueSpan.textContent = `${displayMinValue}" - ${maxValue}"`;
        }
      }
    };

    updateButton(document.querySelector('.active-facets-desktop'));
    updateButton(document.querySelector('.active-facets-mobile'));
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();

    // Custom dimension range filter: Restore range values after additional elements are rendered
    FacetFiltersForm.restoreDimensionRange();
  }

  /**
   * Custom dimension range filter: Get all control elements (inputs, highlight bar, tooltips)
   * for a given dimension range container (works for both desktop and mobile versions)
   */
  static getDimensionRangeControls(container) {
    if (!container) {
      // Fallback: try to find by ID (for backward compatibility)
      const minInput = document.getElementById('dimension-min');
      const maxInput = document.getElementById('dimension-max');
      const minNumberInput = document.getElementById('dimension-input-min');
      const maxNumberInput = document.getElementById('dimension-input-max');
      const highlight = document.getElementById('dimensionRangeHighlight');
      const minOutput = document.getElementById('rangeValueMin');
      const maxOutput = document.getElementById('rangeValueMax');
      return { minInput, maxInput, minNumberInput, maxNumberInput, highlight, minOutput, maxOutput };
    }

    // Find elements by class within container (more reliable than ID)
    const minInput = container.querySelector('.dimension-range-input--min') || container.querySelector('#dimension-min');
    const maxInput = container.querySelector('.dimension-range-input--max') || container.querySelector('#dimension-max');
    const minNumberInput = container.querySelector('.dimension-number-input--min') || container.querySelector('#dimension-input-min');
    const maxNumberInput = container.querySelector('.dimension-number-input--max') || container.querySelector('#dimension-input-max');
    const highlight = container.querySelector('.dimension-range-highlight') || container.querySelector('#dimensionRangeHighlight');
    const minOutput = container.querySelector('.range-value--min') || container.querySelector('#rangeValueMin');
    const maxOutput = container.querySelector('.range-value--max') || container.querySelector('#rangeValueMax');
    return { minInput, maxInput, minNumberInput, maxNumberInput, highlight, minOutput, maxOutput };
  }

  /**
   * Custom dimension range filter: Initialize event listeners for min/max range inputs
   * Attaches input handlers and prevents duplicate initialization
   */
  static initializeDimensionRangeControls(container) {
    if (!container) return;

    const controls = FacetFiltersForm.getDimensionRangeControls(container);
    const { minInput, maxInput, minNumberInput, maxNumberInput } = controls;
    if (!minInput || !maxInput || !minNumberInput || !maxNumberInput) return;

    // Check if already initialized - if yes, just update display
    if (container.dataset.rangeInitialized === 'true') {
      FacetFiltersForm.updateDimensionRangeDisplay(container);
      return;
    }

    container.dataset.rangeInitialized = 'true';


    // number inputs
    minNumberInput.addEventListener('input', (e) => {
      clearTimeout(container.minTypingTimer);
      container.minTypingTimer = setTimeout(() => {
        let val = Number(minNumberInput.value);
        val = Math.round(val / 5) * 5;
        minNumberInput.value = val;
        
        FacetFiltersForm.handleDimensionNumberInput('min', container);
        const controls = FacetFiltersForm.getDimensionRangeControls(container);
        FacetFiltersForm.debouncedApplyDimensionFilters.clear();
        FacetFiltersForm.applyDimensionFilters(Number(controls.minNumberInput.value), Number(controls.maxNumberInput.value));
      }, 800);
    });

    maxNumberInput.addEventListener('input', (e) => {
      clearTimeout(container.maxTypingTimer);
      container.maxTypingTimer = setTimeout(() => {
        let val = Number(maxNumberInput.value);
        val = Math.round(val / 5) * 5;
        maxNumberInput.value = val;
        
        FacetFiltersForm.handleDimensionNumberInput('max', container);
        const controls = FacetFiltersForm.getDimensionRangeControls(container);
        FacetFiltersForm.debouncedApplyDimensionFilters.clear();
        FacetFiltersForm.applyDimensionFilters(Number(controls.minNumberInput.value), Number(controls.maxNumberInput.value));
      }, 800);
    });

    minNumberInput.addEventListener('change', (e) => {
      clearTimeout(container.minTypingTimer);
      const controls = FacetFiltersForm.getDimensionRangeControls(container);
      if (controls.minNumberInput && controls.maxNumberInput) {
        let minValue = Number(controls.minNumberInput.value);
        minValue = Math.round(minValue / 5) * 5;
        controls.minNumberInput.value = minValue;
        
        FacetFiltersForm.handleDimensionNumberInput('min', container);
        FacetFiltersForm.debouncedApplyDimensionFilters.clear();
        FacetFiltersForm.applyDimensionFilters(Number(controls.minNumberInput.value), Number(controls.maxNumberInput.value));
      }
    });

    maxNumberInput.addEventListener('change', (e) => {
      clearTimeout(container.maxTypingTimer);
      const controls = FacetFiltersForm.getDimensionRangeControls(container);
      if (controls.minNumberInput && controls.maxNumberInput) {
        let maxValue = Number(controls.maxNumberInput.value);
        maxValue = Math.round(maxValue / 5) * 5;
        controls.maxNumberInput.value = maxValue;
        
        FacetFiltersForm.handleDimensionNumberInput('max', container);
        FacetFiltersForm.debouncedApplyDimensionFilters.clear();
        FacetFiltersForm.applyDimensionFilters(Number(controls.minNumberInput.value), Number(controls.maxNumberInput.value));
      }
    });


    //range inputs
    minInput.addEventListener('input', (e) => {
      FacetFiltersForm.handleDimensionRangeInput('min', container);
    });

    maxInput.addEventListener('input', (e) => {
      FacetFiltersForm.handleDimensionRangeInput('max', container);
    });

    // Apply filters immediately when user releases the slider (change event)
    minInput.addEventListener('change', (e) => {
      const controls = FacetFiltersForm.getDimensionRangeControls(container);
      if (controls.minInput && controls.maxInput) {
        const minValue = Number(controls.minInput.value);
        const maxValue = Number(controls.maxInput.value);
        // Clear any pending debounced calls and apply immediately
        FacetFiltersForm.debouncedApplyDimensionFilters.clear();
        FacetFiltersForm.applyDimensionFilters(minValue, maxValue);
      }
    });

    maxInput.addEventListener('change', (e) => {
      const controls = FacetFiltersForm.getDimensionRangeControls(container);
      if (controls.minInput && controls.maxInput) {
        const minValue = Number(controls.minInput.value);
        const maxValue = Number(controls.maxInput.value);
        // Clear any pending debounced calls and apply immediately
        FacetFiltersForm.debouncedApplyDimensionFilters.clear();
        FacetFiltersForm.applyDimensionFilters(minValue, maxValue);
      }
    });
    // Initial display update
    FacetFiltersForm.updateDimensionRangeDisplay(container);
  }

  /**
   * Custom dimension range filter: Handle input changes from min/max range sliders
   * Prevents min/max from overlapping, synchronizes all range containers, and applies filters
   */
  static handleDimensionRangeInput(type, container) {
    const controls = FacetFiltersForm.getDimensionRangeControls(container);
    const { minInput, maxInput, minNumberInput, maxNumberInput } = controls;
    if (!minInput || !maxInput || !minNumberInput || !maxNumberInput) return;

    const step = Number(minInput.step) || 1;
    let minValue = Number(minInput.value);
    let maxValue = Number(maxInput.value);

    // Prevent min and max from overlapping - snap back by one step if they meet
    if (type === 'min') {
      if (minValue >= maxValue) {
        minValue = maxValue - step;
        minInput.value = minValue;
        minNumberInput.value = minValue;
      }
    } else if (type === 'max') {
      if (maxValue <= minValue) {
        maxValue = minValue + step;
        maxInput.value = maxValue;
        maxNumberInput.value = maxValue
      }
    }
    //update another inout pair
    minNumberInput.value = minValue;
    maxNumberInput.value = maxValue;
    // Update display for current container
    FacetFiltersForm.updateDimensionRangeDisplay(container);

    // Synchronize all other dimension-range containers (desktop/mobile)
    const allContainers = document.querySelectorAll('.dimension-range-wrapper');
    allContainers.forEach((otherContainer) => {
      if (otherContainer !== container) {
        const otherControls = FacetFiltersForm.getDimensionRangeControls(otherContainer);
        if (otherControls.minInput && otherControls.maxInput && otherControls.minNumberInput && otherControls.maxNumberInput) {
          otherControls.minInput.value = minValue;
          otherControls.maxInput.value = maxValue;
          otherControls.minNumberInput.value = minValue;
          otherControls.maxNumberInput.value = maxValue;
          FacetFiltersForm.updateDimensionRangeDisplay(otherContainer);
        }
      }
    });

    // Apply filters by checking/unchecking dimension checkboxes (debounced to prevent lag while dragging)
    FacetFiltersForm.debouncedApplyDimensionFilters(minValue, maxValue);
  }

  static handleDimensionNumberInput(type, container) {
    const controls = FacetFiltersForm.getDimensionRangeControls(container);
    const { minInput, maxInput, minNumberInput, maxNumberInput } = controls;
    if (!minInput || !maxInput || !minNumberInput || !maxNumberInput) return;

    const step = Number(minNumberInput.step) || 1;
    
    const absoluteMin = Number(minNumberInput.min) || 0;
    const absoluteMax = Number(maxNumberInput.max) || 130;
    const minValue = Math.max(Number(minNumberInput.value) || absoluteMin, absoluteMin);
    const maxValue = Math.min(Number(maxNumberInput.value) || absoluteMax, absoluteMax);

    if (type === 'min') {
      if (minValue >= maxValue) {
        minValue = maxValue - step;
        minNumberInput.value = minValue;
        minInput.value = minValue;
      }
    } else if (type === 'max') {
      if (maxValue <= minValue) {
        maxValue = minValue + step;
        maxNumberInput.value = maxValue;
        maxInput.value = maxValue;
      }
    }
    //update another pair inputs
    minInput.value = minValue;
    maxInput.value = maxValue;
    FacetFiltersForm.updateDimensionRangeDisplay(container);

    // Synchronize all other dimension-range containers (desktop/mobile)
    const allContainers = document.querySelectorAll('.dimension-range-wrapper');
    allContainers.forEach((otherContainer) => {
      if (otherContainer !== container) {
        const otherControls = FacetFiltersForm.getDimensionRangeControls(otherContainer);
        if (otherControls.minInput && otherControls.maxInput && otherControls.minNumberInput && otherControls.maxNumberInput) {
          otherControls.minInput.value = minValue;
          otherControls.maxInput.value = maxValue;
          otherControls.minNumberInput.value = minValue;
          otherControls.maxNumberInput.value = maxValue;
          FacetFiltersForm.updateDimensionRangeDisplay(otherContainer);
        }
      }
    });

    FacetFiltersForm.debouncedApplyDimensionFilters(minValue, maxValue);
  }

  /**
   * Custom dimension range filter: Update visual display (highlight bar and tooltips)
   * Calculates positions based on min/max values and updates the UI accordingly
   */
  static updateDimensionRangeDisplay(container) {
    const controls = FacetFiltersForm.getDimensionRangeControls(container);
    const { minInput, maxInput, highlight, minOutput, maxOutput } = controls;
    if (!minInput || !maxInput || !highlight) return;

    const absoluteMin = Number(minInput.min) || 0;
    const absoluteMax = Number(maxInput.max) || 130;
    const minValue = Math.min(Number(minInput.value) || absoluteMin, Number(maxInput.value) || absoluteMax);
    const maxValue = Math.max(Number(maxInput.value) || absoluteMax, minValue);

    // Calculate percentage positions for highlight bar
    const minPercent = ((minValue - absoluteMin) * 100) / (absoluteMax - absoluteMin);
    const maxPercent = ((maxValue - absoluteMin) * 100) / (absoluteMax - absoluteMin);

    // Update highlight bar position
    highlight.style.left = `${minPercent}%`;
    highlight.style.right = `${100 - maxPercent}%`;

    // Update tooltip positions and values (shows value with " symbol)
    const updateOutput = (output, percent, value) => {
      if (!output) return;
      const newPosition = 10 - percent * 0.2;
      output.innerHTML = `<span>${value}"</span>`;
      output.style.left = `calc(${percent}% + (${newPosition}px))`;
    };

    updateOutput(minOutput, minPercent, minValue);
    updateOutput(maxOutput, maxPercent, maxValue);
  }

  /**
   * Custom dimension range filter: Apply filters by checking/unchecking dimension checkboxes
   * Finds all checkboxes whose ranges fall within the selected min/max bounds and triggers filter update
   * This is debounced to prevent excessive filter updates while dragging the slider
   */
  static applyDimensionFilters(minValue, maxValue) {
    // Gather all dimension values from the checkbox inputs
    const dimensionList = document.querySelector('fieldset[filter-item="Dimension"]');
    if (!dimensionList) return;

    const dimensionItems = dimensionList.querySelectorAll('li input[type="checkbox"]');

    const triggerFilterUpdate = (eventTarget) => {
      const sortFilterForms = document.querySelectorAll('facet-filters-form form');
      const forms = [];
      const form = eventTarget ? eventTarget.closest('form') : null;
      const isMobile = form && form.id === 'FacetFiltersFormMobile';
      sortFilterForms.forEach((f) => {
        if (!isMobile) {
          if (f.id === 'FacetSortForm' || f.id === 'FacetFiltersForm' || f.id === 'FacetSortDrawerForm') {
            const facetForm = document.querySelector('facet-filters-form');
            if (facetForm) {
              forms.push(facetForm.createSearchParams(f));
            }
          }
        } else if (f.id === 'FacetFiltersFormMobile') {
          const facetForm = document.querySelector('facet-filters-form');
          if (facetForm) {
            forms.push(facetForm.createSearchParams(f));
          }
        }
      });

      if (forms.length > 0) {
        const facetForm = document.querySelector('facet-filters-form');
        if (facetForm) {
          const checkboxEvent = {
            target: eventTarget || document.querySelector('#dimension-min'),
            srcElement: eventTarget || document.querySelector('#dimension-min'),
            preventDefault: () => { },
          };
          facetForm.onSubmitForm(forms.join('&'), checkboxEvent);
        }
      }
    };

    // Collect all checkboxes whose ranges OVERLAP with the selected bounds
    // A bucket [A, B] overlaps with [minValue, maxValue] if A <= maxValue AND B >= minValue
    // This is more inclusive than strict containment (A >= minValue && B <= maxValue)
    // and correctly handles cases like selecting 120-130 when the bucket is labeled "101-130"
    const foundCheckboxes = [];
    dimensionItems.forEach((checkbox) => {
      const labelEl = checkbox.closest('label');
      const textEl = labelEl ? labelEl.querySelector('.facet-checkbox__text-label') : null;
      const textValue = textEl ? textEl.textContent : checkbox.value;
      const range = FacetFiltersForm.parseDimensionValue(textValue);
      if (range && range.min <= maxValue && range.max >= minValue) {
        foundCheckboxes.push(checkbox);
      }
    });

    // Reset every checkbox to unchecked
    dimensionItems.forEach((checkbox) => {
      checkbox.checked = false;
    });

    // Check every matching checkbox
    if (foundCheckboxes.length > 0) {
      const checkedValues = [];
      foundCheckboxes.forEach((checkbox) => {
        checkbox.checked = true;
        checkedValues.push(checkbox.value);
      });

      // Clear any fake input
      let fakeInput = dimensionList.querySelector('input.fake-dimension-filter');
      if (fakeInput) fakeInput.checked = false;

      // Store the range value before triggering filtering
      FacetFiltersForm.dimensionRangeValue = { min: minValue, max: maxValue };
      // Trigger filtering after updating the checked state
      setTimeout(() => {
        triggerFilterUpdate(foundCheckboxes[0]);
      }, 0);
    } else {
      FacetFiltersForm.dimensionRangeValue = { min: minValue, max: maxValue };

      // Determine the absolute limits of the range sliders
      const minInput = document.querySelector('.dimension-range-input--min');
      const maxInput = document.querySelector('.dimension-range-input--max');
      const absoluteMin = minInput ? (Number(minInput.min) || 0) : 0;
      const absoluteMax = maxInput ? (Number(maxInput.max) || 130) : 130;

      let fakeInput = dimensionList.querySelector('input.fake-dimension-filter');
      
      // If the user actually moved the sliders, they expect filtering (even if to 0 results)
      if ((minValue > absoluteMin || maxValue < absoluteMax) && dimensionItems.length > 0) {
        if (!fakeInput) {
          fakeInput = document.createElement('input');
          fakeInput.type = 'checkbox';
          fakeInput.className = 'fake-dimension-filter hidden';
          fakeInput.name = dimensionItems[0].name;
          fakeInput.value = 'NO_MATCH_FOUND';
          dimensionList.appendChild(fakeInput);
        }
        fakeInput.checked = true;
      } else {
        // Sliders are reset to default bounds mapping everything
        if (fakeInput) fakeInput.checked = false;
      }

      setTimeout(() => {
        triggerFilterUpdate(fakeInput || dimensionList);
      }, 0);
    }
  }

  /**
   * Custom dimension range filter: Reset range values to initial state (min=0, max=default)
   * Used when clearing all filters to reset the custom range sliders
   */
  static resetDimensionRange() {
    // Clear stored value
    FacetFiltersForm.dimensionRangeValue = null;

    // Find all dimension-range containers and reset their values
    const dimensionRanges = document.querySelectorAll('.dimension-range-wrapper');
    dimensionRanges.forEach((container) => {
      const controls = FacetFiltersForm.getDimensionRangeControls(container);
      const { minInput, maxInput, minNumberInput, maxNumberInput } = controls;
      if (!minInput || !maxInput || !minNumberInput || !maxNumberInput) return;

      // Reset to initial values from HTML attributes
      const defaultMin = Number(minInput.min) || 0;
      const defaultMax = Number(maxInput.max) || 130;
      minInput.value = defaultMin;
      maxInput.value = defaultMax;
      minNumberInput.value = defaultMin;
      maxNumberInput.value = defaultMax;
      // Update display
      FacetFiltersForm.updateDimensionRangeDisplay(container);
    });
  }

  /**
   * Custom dimension range filter: Restore range values after AJAX updates or page load
   * Initializes all dimension range containers (desktop and mobile) and syncs their values
   * Values are restored from stored state or derived from checked checkboxes
   */
  static restoreDimensionRange() {
    // Find all dimension-range containers (desktop and mobile)
    const dimensionRanges = document.querySelectorAll('.dimension-range-wrapper');

    if (dimensionRanges.length === 0) return;

    let dynamicAbsoluteMin = 0;
    let dynamicAbsoluteMax = 130;
    
    const dimensionList = document.querySelector('fieldset[filter-item="Dimension"]');
    if (dimensionList) {
      const allCheckboxes = dimensionList.querySelectorAll('li input[type="checkbox"]');
      if (allCheckboxes.length > 0) {
        let absMin = Number.MAX_SAFE_INTEGER;
        let absMax = 0;
        allCheckboxes.forEach((checkbox) => {
          const labelEl = checkbox.closest('label');
          const textEl = labelEl ? labelEl.querySelector('.facet-checkbox__text-label') : null;
          const textValue = textEl ? textEl.textContent : checkbox.value;
          const range = FacetFiltersForm.parseDimensionValue(textValue);
          if (range) {
            if (range.min < absMin) absMin = range.min;
            if (range.max > absMax) absMax = range.max;
          }
        });
        if (absMin !== Number.MAX_SAFE_INTEGER && absMax !== 0) {
          dynamicAbsoluteMin = Math.max(0, absMin - (absMin % 5));
          dynamicAbsoluteMax = Math.ceil(absMax / 5) * 5;
        }
      }
    }

    dimensionRanges.forEach((container) => {
      const controls = FacetFiltersForm.getDimensionRangeControls(container);
      const { minInput, maxInput, minNumberInput, maxNumberInput } = controls;
      if (!minInput || !maxInput || !minNumberInput || !maxNumberInput) return;
      
      minInput.min = dynamicAbsoluteMin;
      minInput.max = dynamicAbsoluteMax;
      maxInput.min = dynamicAbsoluteMin;
      maxInput.max = dynamicAbsoluteMax;
      minNumberInput.min = dynamicAbsoluteMin;
      minNumberInput.max = dynamicAbsoluteMax;
      maxNumberInput.min = dynamicAbsoluteMin;
      maxNumberInput.max = dynamicAbsoluteMax;
    });

    let minValue = null;
    let maxValue = null;

    // If value was stored, use it
    if (FacetFiltersForm.dimensionRangeValue) {
      minValue = FacetFiltersForm.dimensionRangeValue.min;
      maxValue = FacetFiltersForm.dimensionRangeValue.max;
    } else {
      // Try to read exactly what the user selected from the URL on page load
      const urlParams = new URLSearchParams(window.location.search);
      const urlMin = urlParams.get('dimension-min') || urlParams.get('dimension-input-min');
      const urlMax = urlParams.get('dimension') || urlParams.get('dimension-max') || urlParams.get('dimension-input-max');

      if (urlMin !== null && urlMax !== null) {
        minValue = parseInt(urlMin, 10);
        maxValue = parseInt(urlMax, 10);
      } else if (dimensionList) {
        // If no value was stored and no URL params exist, derive it from the checked checkboxes
        const checkedCheckboxes = dimensionList.querySelectorAll('li input[type="checkbox"]:checked');
        if (checkedCheckboxes.length > 0) {
          let minVal = Number.MAX_SAFE_INTEGER;
          let maxVal = 0;
          checkedCheckboxes.forEach((checkbox) => {
            const labelEl = checkbox.closest('label');
            const textEl = labelEl ? labelEl.querySelector('.facet-checkbox__text-label') : null;
            const textValue = textEl ? textEl.textContent : checkbox.value;
            const range = FacetFiltersForm.parseDimensionValue(textValue);
            if (range) {
              if (range.min < minVal) minVal = range.min;
              if (range.max > maxVal) maxVal = range.max;
            }
          });

          // Set values if we found valid ranges
          if (minVal !== Number.MAX_SAFE_INTEGER && maxVal !== 0) {
            minValue = minVal;
            maxValue = maxVal;
          }
        }
      }
    }

    // Initialize and update all dimension-range containers
    dimensionRanges.forEach((container) => {
      const controls = FacetFiltersForm.getDimensionRangeControls(container);
      const { minInput, maxInput, minNumberInput, maxNumberInput } = controls;
      if (!minInput || !maxInput || !minNumberInput || !maxNumberInput) return;

      if (minValue !== null && maxValue !== null) {
        minInput.value = minValue;
        maxInput.value = maxValue;
        minNumberInput.value = minValue;
        maxNumberInput.value = maxValue;
        FacetFiltersForm.dimensionRangeValue = { min: minValue, max: maxValue };
      } else {
        // Default to min/max if no checkboxes are checked
        // Only set dimensionRangeValue if it was previously set (to preserve reset state)
        const defaultMin = Number(minInput.min) || 0;
        const defaultMax = Number(maxInput.max) || 130;
        minInput.value = defaultMin;
        maxInput.value = defaultMax;
        minNumberInput.value = defaultMin;
        maxNumberInput.value = defaultMax;

        // Don't set dimensionRangeValue here - keep it null if it was reset
      }

      // Initialize after setting values
      FacetFiltersForm.initializeDimensionRangeControls(container);

      // Update display after initialization
      FacetFiltersForm.updateDimensionRangeDisplay(container);
    });
  }

  static toggleCollectionLoading(isLoading, source = 'unknown') {
    const productGridContainer = document.getElementById('ProductGridContainer');
    if (!productGridContainer) return;

    productGridContainer.querySelectorAll('.collection').forEach((collection, collectionIndex) => {
      collection.classList.toggle('loading', isLoading);
      collection.querySelectorAll('.loading-overlay').forEach((overlay, overlayIndex) => {
        if (isLoading) {
          overlay.style.removeProperty('display');
        } else {
          overlay.style.removeProperty('display');
        }
      });
    });
  }


  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      // Skip replacing the Dimension filter's wrap - the slider manages its own checkbox state
      const isDimensionFilter = targetWrapElement.getAttribute('filter-item') === 'Dimension';
      if (!isDimensionFilter) {
        const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
        if (isShowingMore) {
          sourceWrapElement
            .querySelectorAll('.facets__item.hidden')
            .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
        }

        targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
      }
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const target = event.target || event.srcElement;
    // Custom dimension range filter: Skip dimension range inputs - they are handled by direct event listeners
    // This prevents duplicate handling since range inputs have their own input event handlers
    if (target && (target.classList.contains('dimension-range-input') || target.getAttribute('data-facet-type') === 'dimension')) {
      return;
    }

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);

    // Custom dimension range filter: Reset range to initial values when clearing all filters
    // Check if this is "Remove all" by checking if the URL has no query parameters
    if (!url || url.trim() === '') {
      FacetFiltersForm.resetDimensionRange();
    }

    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
// Custom dimension range filter: Stores the current min/max range values to persist after AJAX updates
FacetFiltersForm.dimensionRangeValue = null;

// Custom dimension range filter: Debounced version of applyDimensionFilters to prevent lag while dragging
// Only applies filters after user stops dragging for 300ms, always using the latest values
FacetFiltersForm.debouncedApplyDimensionFilters = (() => {
  let timeoutId = null;
  let lastMinValue = null;
  let lastMaxValue = null;
  const debouncedFn = (minValue, maxValue) => {
    // Store the latest values
    lastMinValue = minValue;
    lastMaxValue = maxValue;

    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      // Apply filters with the latest stored values
      if (lastMinValue !== null && lastMaxValue !== null) {
        FacetFiltersForm.applyDimensionFilters(lastMinValue, lastMaxValue);
      }
    }, 300);
  };
  // Expose method to clear pending timeout
  debouncedFn.clear = () => {
    clearTimeout(timeoutId);
    timeoutId = null;
  };
  return debouncedFn;
})();

customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// Custom dimension range filter: Restore the dimension range when the page loads
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
      FacetFiltersForm.restoreDimensionRange();
      FacetFiltersForm.updateDimensionRangeDisplayValue();
    }, 500);
  });
} else {
  setTimeout(() => {
    FacetFiltersForm.restoreDimensionRange();
    FacetFiltersForm.updateDimensionRangeDisplayValue();
  }, 500);
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();

    // If this is a dimension item, trigger click on "Remove all" button instead
    if (this.hasAttribute('data-dimension-item')) {
      event.stopPropagation();

      // Find the "Remove all" button (facet-remove with active-facets__button-wrapper class)
      const removeAllButton = document.querySelector('facet-remove.active-facets__button-wrapper');
      if (removeAllButton) {
        const removeAllLink = removeAllButton.querySelector('a');
        if (removeAllLink) {
          // Trigger click on the "Remove all" link
          removeAllLink.click();
        }
      }
      return;
    }

    // Default behavior for non-dimension items
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }

}

customElements.define('facet-remove', FacetRemove);
